import { useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend 
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, Info, Download } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Button } from '../components/ui/button';

// Utility function to convert data to CSV
function convertToCSV(data, columns) {
  if (!data || data.length === 0) return '';
  
  // Header row
  const headers = columns.map(col => `"${col.label}"`).join(',');
  
  // Data rows
  const rows = data.map(row => 
    columns.map(col => {
      const value = row[col.key];
      if (value === null || value === undefined) return '""';
      if (typeof value === 'string') return `"${value.replace(/"/g, '""')}"`;
      return `"${value}"`;
    }).join(',')
  );
  
  return [headers, ...rows].join('\n');
}

// Utility function to download CSV
function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const COLORS = ['#8B5CF6', '#EC4899', '#14B8A6', '#EAB308', '#3B82F6', '#F97316'];

export default function AnalyticsDisplay({ data }) {
  // Export table to CSV - defined before early return to follow hook rules
  const handleExportCSV = useCallback(() => {
    if (!data?.table?.rows || data.table.rows.length === 0) return;
    
    const csvContent = convertToCSV(data.table.rows, data.table.columns);
    const filename = `${data.title || 'analytics'}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename.replace(/[^a-z0-9_.-]/gi, '_'));
  }, [data]);

  if (!data) return null;

  const { title, kpis, table, charts, insights, data_lineage } = data;

  return (
    <div className="space-y-4" data-testid="analytics-display">
      {/* Title with Export Button */}
      {title && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          {table && table.rows && table.rows.length > 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExportCSV}
              className="flex items-center gap-2"
              data-testid="export-csv-button"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          )}
        </div>
      )}

      {/* KPI Cards */}
      {kpis && kpis.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpis.map((kpi, idx) => (
            <Card key={idx} className="bg-card/50 backdrop-blur-sm border-border/50">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-1">{kpi.label}</p>
                <p className="text-2xl font-bold">{kpi.value}</p>
                {kpi.delta && (
                  <div className={cn(
                    'flex items-center gap-1 text-sm mt-1',
                    kpi.trend === 'up' ? 'text-accent' : kpi.trend === 'down' ? 'text-destructive' : 'text-muted-foreground'
                  )}>
                    {kpi.trend === 'up' ? <TrendingUp className="w-3 h-3" /> : 
                     kpi.trend === 'down' ? <TrendingDown className="w-3 h-3" /> : 
                     <Minus className="w-3 h-3" />}
                    {kpi.delta}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Charts */}
      {charts && charts.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {charts.map((chart, idx) => (
            <Card key={idx} className="bg-card/50 backdrop-blur-sm border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{chart.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartRenderer chart={chart} data={table?.rows || []} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Data Table */}
      {table && table.rows && table.rows.length > 0 && (
        <Card className="bg-card/50 backdrop-blur-sm border-border/50 overflow-hidden">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Data Table ({table.rows.length} rows)</CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleExportCSV}
              className="flex items-center gap-1 text-xs"
              data-testid="table-export-button"
            >
              <Download className="w-3 h-3" />
              CSV
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50 bg-muted/30">
                    {table.columns.map((col) => (
                      <th
                        key={col.key}
                        className="px-4 py-3 text-left text-sm font-semibold"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, rowIdx) => (
                    <tr
                      key={rowIdx}
                      className="border-b border-border/30 last:border-0 hover:bg-muted/20 transition-colors"
                    >
                      {table.columns.map((col) => (
                        <td key={col.key} className="px-4 py-3 text-sm">
                          {formatValue(row[col.key], col.type)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Insights */}
      {insights && insights.length > 0 && (
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Info className="w-4 h-4 text-primary" />
              Key Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {insights.map((insight, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                  {insight}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Data Lineage */}
      {data_lineage && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground">
              View Data Lineage
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="p-3 rounded-lg bg-muted/30 text-xs font-mono mt-2">
              <p><strong>Brand:</strong> {data_lineage.brand_id}</p>
              <p><strong>Collections:</strong> {data_lineage.collections?.join(', ')}</p>
              {data_lineage.time_range && Object.keys(data_lineage.time_range).length > 0 && (
                <p><strong>Time Range:</strong> {JSON.stringify(data_lineage.time_range)}</p>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

function ChartRenderer({ chart, data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.slice(0, 10); // Limit to 10 items for readability
  }, [data]);

  if (chartData.length === 0) {
    return <p className="text-center text-muted-foreground py-8">No data available</p>;
  }

  const { type, x_key, y_keys } = chart;

  switch (type) {
    case 'bar':
      return (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey={x_key} 
              tick={{ fontSize: 12 }} 
              stroke="hsl(var(--muted-foreground))"
            />
            <YAxis 
              tick={{ fontSize: 12 }} 
              stroke="hsl(var(--muted-foreground))"
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }}
            />
            {y_keys.map((key, idx) => (
              <Bar key={key} dataKey={key} fill={COLORS[idx % COLORS.length]} radius={[4, 4, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );

    case 'line':
      return (
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey={x_key} 
              tick={{ fontSize: 12 }} 
              stroke="hsl(var(--muted-foreground))"
            />
            <YAxis 
              tick={{ fontSize: 12 }} 
              stroke="hsl(var(--muted-foreground))"
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }}
            />
            {y_keys.map((key, idx) => (
              <Line 
                key={key} 
                type="monotone" 
                dataKey={key} 
                stroke={COLORS[idx % COLORS.length]} 
                strokeWidth={2}
                dot={{ fill: COLORS[idx % COLORS.length] }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );

    case 'pie':
      return (
        <ResponsiveContainer width="100%" height={250}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey={y_keys[0]}
              nameKey={x_key}
              cx="50%"
              cy="50%"
              outerRadius={80}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              labelLine={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );

    default:
      return <p className="text-center text-muted-foreground">Unsupported chart type</p>;
  }
}

function formatValue(value, type) {
  if (value === null || value === undefined) return '-';
  
  switch (type) {
    case 'currency':
      return typeof value === 'number' ? `₹${value.toLocaleString()}` : value;
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : value;
    case 'percent':
      return typeof value === 'number' ? `${value.toFixed(1)}%` : value;
    case 'date':
      return new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
    default:
      return value;
  }
}
